import { Avatar, Box, IconButton, Typography } from "@mui/material";
import Linkify from "linkify-react";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { isBrowser, isIOS, isMobile, isTablet } from "react-device-detect";
import { renderToString } from "react-dom/server";
import { CDN_ONE_FILE_PATH, CHAT_HEADER_HEIGHT, extractImgTags, hasDayChanged, HtmlDecode, icon, isNullOrEmpty, objReactions, SELLER, UserStats, WEBSITE_PATH, windowPostMessage } from "../../common/common";
import { replaceAllData } from "../../common/commonJS";
import { Decrypt } from "../../common/reacte2ee";
import { resource } from "../../common/resource";
import CreateMenu, { CreateReactionsMenu } from "../../components/menu";
import { ReceiverDetailsContext } from "../../contexts/receiverDetailsContext";
import { MessageData, Reactions, User } from "../../models/messaging";
import { formatDateTime, formatDateTimeMessage } from "../dateFormatter";
import { copyMenuItem, deleteMenuItem, MenuItems, replyMenuItem } from "../menuItems/chatMenuItems";
import ChatNullset from "../nullSets/chatNullset";
import useLongPressMod from "../useLongPress";
import ChatReply from "./chatReply";
import ChatSendHi from "./chatSendHi";
import ChatWarning from "./chatWarning";
import ShowReactions from "./reaction";

interface MessageProps {
  messages: MessageData[];
  chatWarningRef: any;
  selectedMessages: MessageData[];
  receiverID: string;
  senderID: string;
  senderProfileDetails: User;
  messageLimitReached: boolean;
  blockedAccount: boolean;
  adminIsBlocked: boolean;
  chatFooterHeight: number;
  chatReactionRef: any;
  onScrollEvent: any;
  onMessageLongPress: any;
  onWavingHandClick: any;
  updateFollowStatus: any;
  onUpdateReaction: any;
  clearSelectedMessages: any;
  onReplyClick: any;
  onDeleteMessageClick: any;
  onMessageSingleClick: any;
  isAllowedToSendMessage: any;
}
let timer: any = null;
const ChatMessage = ({ messages, chatWarningRef, receiverID, senderID, senderProfileDetails, messageLimitReached, blockedAccount, adminIsBlocked, chatFooterHeight, chatReactionRef, onScrollEvent, onMessageLongPress, onWavingHandClick, selectedMessages, updateFollowStatus, onUpdateReaction, clearSelectedMessages, onReplyClick, onDeleteMessageClick, onMessageSingleClick, isAllowedToSendMessage }: MessageProps, ref: any) => {
  let nextMessageReceiverID: string | undefined = messages.length > 0 ? messages[0].recipientUcnum : "";
  const [showWarning, setShowWarning] = useState<boolean>(blockedAccount ? false : true);
  const [insetPadding, setInsetPadding] = useState<number>(0);
  const [chatOptionsAnchor, setChatOptionsAnchor] = useState<any>();
  const [chatReactionListAnchor, setChatReactionListAnchor] = useState<any>();
  const [chatReactionsAnchor, setChatReactionsAnchor] = useState<any>();

  const { receiverDetails } = useContext<any>(ReceiverDetailsContext);

  const chatMenuRef: any = useRef<any>();
  const isScrolling: any = useRef<boolean>(false);

  useImperativeHandle(ref, () => ({
    getChatScrollPosition: () => {
      const chatArea: any = document.getElementsByClassName("chatArea");
      if (chatArea) {
        return chatArea[0].scrollTop;
      } else {
        return -1;
      }
    },
    scrollChatToPosition: (position: number) => {
      const chatArea: any = document.getElementsByClassName("chatArea");
      if (chatArea) {
        chatArea[0].scrollTop = position;
      }
    },
    scrollChatToBottom: (animate: boolean = false) => {
      const chatArea: any = document.getElementsByClassName("chatArea");
      if (chatArea) {
        if (animate) {
          chatArea[0].scrollTo({ top: chatArea[0].scrollHeight, behavior: "smooth" });
        } else {
          chatArea[0].scrollTop = chatArea[0].scrollHeight;
        }
      }
    },
    isEnd: () => {
      const chatArea: any = document.getElementsByClassName("chatArea");
      if (chatArea) {
        return chatArea[0].scrollHeight - Math.round(chatArea[0].scrollTop) === chatArea[0].clientHeight;
      } else {
        return false;
      }
    },
    getLastMessageElement: () => {
      const chatFullMessage: any = document.getElementsByClassName("chatFullMessage");
      if (chatFullMessage) {
        return chatFullMessage[chatFullMessage.length - 1];
      } else {
        return false;
      }
    },
    showMessageWarning: () => {
      setShowWarning(true);
    },
    hideMessageWarning: () => {
      setShowWarning(false);
    },
    hideMessageMenus: () => {
      setChatReactionsAnchor(null);
      setChatOptionsAnchor(null);
    },
    setPadding: (padding: number) => {
      setInsetPadding(padding);
    },
  }));
  const renderLink = ({ attributes, content }: any) => {
    const { href, ...props } = attributes;
    return isBrowser ? (
      // eslint-disable-next-line
      <a className={`mdc-underline ${props.class}`} data-to={href} style={{ cursor: "pointer" }}>
        {content}
      </a>
    ) : (
      // eslint-disable-next-line
      <a href={href} className={`mdc-underline ${props.class}`}>
        {content}
      </a>
    );
  };
  const MobileLongPress = () => {
    const onLongPress = (e: any) => {
      if (!isBrowser && e.target.closest(".chatSingleMessage").attributes["data-id"]) {
        const messageID: string = e.target.closest(".chatSingleMessage").attributes["data-id"].value;
        const data: MessageData | undefined = messages.find((el) => el.messageID === messageID);
        onMessageLongPress && onMessageLongPress(messageID);
        if (selectedMessages.length === 0 && data?.deletedBy !== "Both") {
          onReactionsClick(e, e.target.closest(".chatSingleMessage").attributes["data-index"].value, messageID);
        }
      }
    };
    const defaultOptions = {
      shouldPreventDefault: false,
      delay: 1000,
    };
    return useLongPressMod(
      onLongPress,
      (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        onMessageSingleClick();
        if (selectedMessages.length > 0) {
          setChatReactionsAnchor(null);
          const messageID = e.target.closest(".chatSingleMessage").attributes["data-id"].value;
          onMessageLongPress && onMessageLongPress(messageID, true);
          return;
        }
        if (!isScrolling.current) {
          const currentTarget: any = e.currentTarget;
          if (currentTarget.getAttribute("data-type") === "3") {
            onPostChatClick(currentTarget.getAttribute("data-link"), currentTarget.getAttribute("data-post-link"));
          } else if (currentTarget.getAttribute("data-type") === "4") {
            windowPostMessage({ type: "postClick", postLink: currentTarget.getAttribute("data-link") }, WEBSITE_PATH);
          } else {
            if (e.target.nodeName === "A") {
              onPostChatClick("", e.target.getAttribute("href"));
            }
          }
        }
      },
      defaultOptions
    );
  };

  const onChatMessageClick = (e: any) => {
    if (isBrowser && !isNullOrEmpty(e.target.getAttribute("data-to"))) {
      windowPostMessage({ type: "postClick", postLink: e.target.getAttribute("data-to") }, WEBSITE_PATH);
    }
  };
  const onChatMessageHover = (event: any) => {
    if (event.currentTarget.querySelector(".reaction-menu:not(.active)") && isBrowser) {
      event.currentTarget.querySelector(".reaction-menu").style.opacity = "1";
    }
    if (event.currentTarget.querySelector(".options-menu:not(.active)") && isBrowser) {
      event.currentTarget.querySelector(".options-menu").style.opacity = "1";
    }
  };
  const onChatMessageLeave = (event: any) => {
    if (event.currentTarget.querySelector(".reaction-menu:not(.active)") && isBrowser) {
      event.currentTarget.querySelector(".reaction-menu").style.opacity = "0";
    }
    if (event.currentTarget.querySelector(".options-menu:not(.active)") && isBrowser) {
      event.currentTarget.querySelector(".options-menu").style.opacity = "0";
    }
  };
  const onScrollEnd = () => {
    isScrolling.current = false;
  };
  const onChatScroll = (event: any) => {
    isScrolling.current = true;
    chatOptionsAnchor !== null && setChatOptionsAnchor(null);
    chatReactionsAnchor !== null && setChatReactionsAnchor(null);
    if (!isBrowser) {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      timer = setTimeout(function () {
        onScrollEnd();
      }, 150);
    }
    onScrollEvent(event);
  };
  const onChatOptionsClick = (event: any, n: number, messageID: string) => {
    if (isBrowser) {
      event.currentTarget.classList.add("active");
      setChatOptionsAnchor({
        index: n,
        msgID: messageID,
        target: event.currentTarget,
      });
    }
  };
  const onReactionsClick = (event: any, n: number, messageID: string) => {
    const currMessage: MessageData | undefined = messages.find((el) => el.messageID === messageID);
    let reactionIndex = -1;
    if (currMessage) {
      const myReactionFound: Reactions | undefined = currMessage.reactions?.find((el) => el.ucnum === senderID);
      if (myReactionFound) {
        reactionIndex = objReactions.findIndex((el: any) => el.nodeString === myReactionFound.reaction);
      }
    }

    setChatReactionsAnchor({
      index: n,
      msgID: messageID,
      target: event.currentTarget ? event.currentTarget : event.target,
      reactionIndex: reactionIndex,
    });
  };
  const onReactionsSelect = (messageID: string, key: string) => {
    onUpdateReaction(messageID, key, objReactions[parseInt(key, 10) - 1]);
    clearSelectedMessages();
    setChatReactionsAnchor(null);
  };
  const onReactionListClick = (event: any, messageID: string) => {
    if (!chatReactionListAnchor) {
      setChatReactionListAnchor({
        msgID: messageID,
        target: event.target,
      });
    }
  };
  const onCopyClick = (messageContent: string) => {
    navigator.clipboard.writeText(messageContent);
  };
  const onAnimationComplete = () => {
    if (showWarning && chatWarningRef.current && chatWarningRef.current.isWarningFixed()) {
      setInsetPadding(chatWarningRef.current.getWarningHeight());
    } else {
      setInsetPadding(0);
    }
  };
  const onPostChatClick = (eSysObjectID: string, postLink: string) => {
    if (!isBrowser && selectedMessages.length > 0) {
      return;
    }
    windowPostMessage({ type: "postClick", eSysObjectID: eSysObjectID, postLink: postLink }, WEBSITE_PATH);
  };
  const onLinkChatClick = (postLink: string) => {
    windowPostMessage({ type: "postClick", postLink: postLink }, WEBSITE_PATH);
  };
  const onReplyMessageClick = (messageID: string) => {
    const currMessage: MessageData | undefined = messages.find((el) => el.messageID === messageID);
    if (currMessage && !currMessage.deletedBy) {
      const nodeToScroll: any = document.querySelector(`[data-id="${messageID}"]`);
      if (nodeToScroll) {
        nodeToScroll.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => {
          nodeToScroll.classList.add("pulseAnimation");
        }, 500);
        setTimeout(() => {
          nodeToScroll.classList.remove("pulseAnimation");
        }, 2000);
      }
    }
  };

  function filterNodes(element: any) {
    const children = element.childNodes;
    for (let i = children.length - 1; i >= 0; i--) {
      const child = children[i];
      if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== "IMG") {
        // Remove non-<img> elements, but keep their child nodes
        // while (child.firstChild) {
        //     element.insertBefore(child.firstChild, child);
        // }
        element.removeChild(child);
      } else if (child.nodeType !== Node.ELEMENT_NODE && child.nodeType !== Node.TEXT_NODE) {
        // Remove nodes that are not elements or text nodes
        element.removeChild(child);
      }
    }
  }

  const handleCopyEvent = (event: any) => {
    event.preventDefault();
    // Get the current selection
    const selection: any = window.getSelection();
    if (selection.rangeCount > 0) {
      const tempElement = document.createElement("div");
      for (let i = 0; i < selection.rangeCount; i++) {
        const htmlValue: any = selection.getRangeAt(i).cloneContents();
        tempElement.appendChild(htmlValue);
      }
      filterNodes(tempElement);
      const htmlContent = tempElement.innerHTML;
      let clipboardData: any = event.clipboardData;
      clipboardData.setData("text", htmlContent);
    }
  };

  let longPressEvent = !isBrowser ? MobileLongPress() : {};

  useEffect(() => {
    const allReactionElems: any = document.querySelector(".reaction-menu.active");
    if (allReactionElems) {
      const reactMenu: any = document.querySelectorAll(".reaction-menu");
      for (let i = 0; i < reactMenu.length; i++) {
        reactMenu[i].style.opacity = "0";
      }
      allReactionElems.classList.remove("active");
    }
    if (!isNullOrEmpty(chatReactionsAnchor)) {
      chatReactionsAnchor.target.style.opacity = "1";
      chatReactionsAnchor.target.classList.add("active");
    }
  }, [chatReactionsAnchor]);

  useEffect(() => {
    const allOptionsElems: any = document.querySelector(".options-menu.active");
    if (allOptionsElems) {
      const optionsMenu: any = document.querySelectorAll(".options-menu");
      for (let i = 0; i < optionsMenu.length; i++) {
        optionsMenu[i].style.opacity = "0";
      }
      allOptionsElems.classList.remove("active");
    }
    if (!isNullOrEmpty(chatOptionsAnchor)) {
      chatOptionsAnchor.target.style.opacity = "1";
      chatOptionsAnchor.target.classList.add("active");
    }
  }, [chatOptionsAnchor]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (showWarning && chatWarningRef.current && chatWarningRef.current.isWarningFixed()) {
        setInsetPadding(chatWarningRef.current.getWarningHeight());
      } else {
        setInsetPadding(0);
      }
    });
    if (document.getElementsByClassName("chatWarnings")[0]) {
      const config: any = { attributes: true, childList: true, characterData: true };
      resizeObserver.observe(document.getElementsByClassName("chatWarnings")[0], config);
    }
    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener("copy", handleCopyEvent);
    return () => {
      window.removeEventListener("copy", handleCopyEvent);
    };
  }, []);

  return (
    <Box className="chatArea pos-rel" onScroll={onChatScroll} sx={{ height: `calc(100% - ${chatFooterHeight + CHAT_HEADER_HEIGHT}px)` }}>
      {showWarning && ((!SELLER && receiverDetails.entityType === 1 && receiverDetails.followingStatus !== UserStats.BOTH_USERS_FOLLOW) || receiverDetails.entityType === 2 || (SELLER && receiverDetails.status === 1) || isNullOrEmpty(receiverDetails) || (!isNullOrEmpty(receiverDetails) && isNullOrEmpty(receiverDetails.name))) ? <ChatWarning ref={chatWarningRef} onAnimationComplete={onAnimationComplete} adminIsBlocked={adminIsBlocked} messageLimitReached={messageLimitReached} /> : null}
      {messages.length === 0 ? (
        <Box className={`flex flex-direction-column`}>
          {showWarning && receiverDetails.followingStatus === UserStats.BOTH_USERS_FOLLOW && <ChatWarning ref={chatWarningRef} onAnimationComplete={onAnimationComplete} adminIsBlocked={adminIsBlocked} messageLimitReached={messageLimitReached} />}
          <Box sx={{ paddingTop: `${insetPadding}px` }} className="flex-grow flex-start">
            <ChatNullset warningRef={chatWarningRef} showWarning={showWarning} adminIsBlocked={adminIsBlocked} blockedAccount={blockedAccount} updateFollowStatus={updateFollowStatus} />
          </Box>
        </Box>
      ) : (
        <Box className="w-per-100 flex flex-direction-column-reverse justify-content-end flex-grow" sx={{ paddingTop: `${insetPadding}px` }}>
          {messages.map((k: MessageData, i: number) => {
            const lpe: any = !k.notSent && !adminIsBlocked && !blockedAccount ? longPressEvent : {}; //isNullOrEmpty(k.deletedBy) &&
            if (isNullOrEmpty(k.deletedBy)) {
              k.messageContent = Decrypt(k.messageContent);
              k.parent && k.parent.length > 0 && (k.parent[0].messageContent = Decrypt(k.parent[0].messageContent));
              if ((k.messageType === 3 || k.messageType === 4) && typeof k.messageContent === "string" && k.deletedBy !== "Both") {
                k.messageContent = JSON.parse(k.messageContent);
              }
            }
            nextMessageReceiverID = messages[i + 1] ? messages[i + 1].recipientUcnum : k.recipientUcnum;
            const isCombined: boolean = nextMessageReceiverID === k.recipientUcnum;
            const hasDayChangedValue: boolean = i < messages.length - 1 && hasDayChanged(new Date(messages[i + 1].sentAt), new Date(k.sentAt));
            const chatOptionsItems: Array<MenuItems> = k.messageType === 1 && isNullOrEmpty(k.deletedBy) ? [replyMenuItem(() => onReplyClick(k)), copyMenuItem(() => onCopyClick(k.messageContent)), deleteMenuItem(() => onDeleteMessageClick(k))] : k.deletedBy === "Both" ? [deleteMenuItem(() => onDeleteMessageClick(k))] : [replyMenuItem(() => onReplyClick(k)), deleteMenuItem(() => onDeleteMessageClick(k))];
            if (k.messageType === 1 && isNullOrEmpty(k.deletedBy)) {
              const newString: string = k.messageContent.replace(/<\/?[^>]+(>|$)/g, "").trim();
              const count = (k.messageContent.match(/emojis-20/g) || []).length + (k.messageContent.match(/emojis-64/g) || []).length;
              const allImgTags = extractImgTags(k.messageContent);
              if (allImgTags.length > 0) {
                for (let j = 0; j < allImgTags.length; j++) {
                  if (allImgTags[j] && allImgTags[j].getAttribute("src") && allImgTags[j].getAttribute("src")?.includes("/images/emojis/emojis-20.png")) {
                    const objPosition: string = allImgTags[j].outerHTML.includes("object-position") ? allImgTags[j].outerHTML.split("object-position: ")[1].split(";")[0] : "";
                    const ariaLabel: string = allImgTags[j].outerHTML.includes("aria-label") ? allImgTags[j].outerHTML.split(`aria-label="`)[1].split(`" `)[0] : "";
                    let replaceImage: string = `<img src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=" aria-label="${ariaLabel}" width="20" height="20" style=" background-size: 6100% 6100%; background-image:url(${CDN_ONE_FILE_PATH}images/emojis/emojis-64.png); background-position: ${objPosition};">`;
                    k.messageContent = k.messageContent.replace(allImgTags[j].outerHTML, replaceImage);
                  }
                }
                if (isIOS) {
                  k.messageContent = replaceAllData(replaceAllData(k.messageContent, `src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=`, `srcset="" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAgMBAhS+BpQAAAAASUVORK5CYII="`), `background-size: 6100% 6100%`, `object-fit: none; background-size: 6100% 6100%`);
                } else {
                  k.messageContent = replaceAllData(replaceAllData(replaceAllData(k.messageContent, `src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAgMBAhS+BpQAAAAASUVORK5CYII="`, `src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="`), `srcset=""`, ""), `srcSet=""`, "");
                }
              }
              if (newString === "" && count > 0) {
                switch (count) {
                  case 1:
                    k.messageContent = replaceAllData(k.messageContent, `width="20"`, `width="48"`);
                    k.messageContent = replaceAllData(k.messageContent, `width="22"`, `width="48"`);
                    k.messageContent = replaceAllData(k.messageContent, `height="20"`, `height="48"`);
                    break;
                  case 2:
                    k.messageContent = replaceAllData(k.messageContent, `width="20"`, `width="36"`);
                    k.messageContent = replaceAllData(k.messageContent, `width="22"`, `width="36"`);
                    k.messageContent = replaceAllData(k.messageContent, `height="20"`, `height="36"`);
                    break;
                  case 3:
                    k.messageContent = replaceAllData(k.messageContent, `width="20"`, `width="24"`);
                    k.messageContent = replaceAllData(k.messageContent, `width="22"`, `width="24"`);
                    k.messageContent = replaceAllData(k.messageContent, `height="20"`, `height="24"`);
                    break;
                }
              }
            } else if (k.messageType === 2 && k.messageContent && k.messageContent.includes("waving-hand.png") && isNullOrEmpty(k.deletedBy)) {
              k.messageContent = replaceAllData(k.messageContent, `width: 32px`, `width: 24px"`);
              k.messageContent = replaceAllData(k.messageContent, `height: 32px"`, `height: 24px`);
            }

            return k.deletedBy !== senderID ? (
              <Box id={`fm-${i}`} key={i} className={`chatFullMessage bor-t-r-b-l-4 display-flex flex-direction-column ${isCombined ? "" : "mar-t-12"}`}>
                {k.isLast && showWarning && receiverDetails.followingStatus === UserStats.BOTH_USERS_FOLLOW && <ChatWarning ref={chatWarningRef} onAnimationComplete={onAnimationComplete} adminIsBlocked={adminIsBlocked} messageLimitReached={messageLimitReached} />}
                {k.isLast && <ChatNullset warningRef={chatWarningRef} showWarning={showWarning} adminIsBlocked={adminIsBlocked} blockedAccount={blockedAccount} updateFollowStatus={updateFollowStatus} />}
                {hasDayChangedValue ? (
                  <Box className={`w-per-100 display-flex-justify-center ${isMobile ? "noselect" : ""}`}>
                    <Typography variant="caption" className={`message-date text-primary ft-wt-500 mar-t-12 mar-b-12 pad-7`}>
                      {formatDateTime(k.sentAt)}
                    </Typography>
                  </Box>
                ) : k.isLast ? (
                  <Box className={`w-per-100 display-flex-justify-center ${isMobile ? "noselect" : ""}`}>
                    <Typography variant="caption" className={`message-date text-primary ft-wt-500 mar-t-24 mar-b-24 pad-7`}>
                      {formatDateTime(k.sentAt)}
                    </Typography>
                  </Box>
                ) : null}
                <Box
                  data-id={k.messageID}
                  data-index={i}
                  data-type={k.messageType}
                  data-link={k.messageContent ? (k.messageType === 4 ? k.messageContent.url : k.messageContent.eSysObjectID) : ""}
                  data-post-link={k.messageContent ? k.messageContent.postLink : ""}
                  onClick={onChatMessageClick}
                  {...lpe}
                  onMouseOver={!k.notSent && !adminIsBlocked && !blockedAccount ? onChatMessageHover : () => {}}
                  onMouseLeave={!k.notSent && !adminIsBlocked && !blockedAccount ? onChatMessageLeave : () => {}}
                  className={`chatSingleMessage flex align-items-center ${k.messageType === 2 && k.deletedBy !== "Both" && k.messageContent && !k.messageContent.includes("waving-hand.png") ? "pad-t-12" : ""} ${k.recipientUcnum === receiverID ? "flex-direction-row-reverse" : "flex-direction-row"} ${
                    !isBrowser && selectedMessages.findIndex((el) => el.messageID === k.messageID) !== -1 ? "bg-bright-sky-blue" : ""
                  } ${!isBrowser ? "user-select-none" : ""}`}
                >
                  {k.recipientUcnum === receiverID && k.notSent && icon({ iconName: "error", iconColor: "icon-crimson" })}
                  <Box
                    className={`chatHtml flex-direction-column display-flex ${k.recipientUcnum === receiverID ? "chatMessageSender" : "chatMessageReceiver"}`}
                    sx={{
                      padding: k.messageType === 2 && k.deletedBy !== "Both" && k.messageContent && !k.messageContent.includes("waving-hand.png") ? "unset" : "4px",
                      minWidth: k.messageType === 2 && k.deletedBy !== "Both" && k.messageContent && !k.messageContent.includes("waving-hand.png") ? "unset" : k.recipientUcnum === receiverID ? "95px" : "75px",
                      boxShadow: k.messageType === 2 && k.deletedBy !== "Both" && k.messageContent && !k.messageContent.includes("waving-hand.png") ? "unset" : "0px 1px 0px #0000003d",
                      backgroundColor: k.messageType === 2 && k.deletedBy !== "Both" && k.messageContent && !k.messageContent.includes("waving-hand.png") ? "unset" : k.recipientUcnum === receiverID ? "var(--color-pale-cornflower-blue)" : "var(--color-ultra-light-gray)",
                      maxWidth: k.deletedBy !== "Both" ? (k.messageType === 3 ? (isTablet ? "188px !important" : "152px !important") : k.messageType === 4 ? (isTablet ? "248px !important" : "224px !important") : "unset") : "unset",
                    }}
                  >
                    {(hasDayChangedValue || !isCombined || i === messages.length - 1) && (k.messageType !== 2 || (k.messageContent && k.messageContent.includes("waving-hand.png"))) ? <Box className={`${k.recipientUcnum === receiverID ? "talkBubbleTriangleSender" : "talkBubbleTriangleReceiver"}`}></Box> : null}
                    {(k.parent && k.parent.length > 0 && isNullOrEmpty(k.deletedBy)) || (k.parent && k.parent.length > 0 && !isNullOrEmpty(k.deletedBy) && k.deletedBy === k.recipientUcnum) ? <ChatReply senderID={senderID} backgroundColorClass={k.recipientUcnum === receiverID ? "bg-periwinkle-crystal" : "bg-extra-light-gray"} selectedReply={k.parent[0]} noPadding={true} onReplyMessageClick={() => onReplyMessageClick(k.parent ? k.parent[0].messageID : "")} /> : null}
                    {k.messageType === 2 && k.deletedBy !== "Both" && k.messageContent && !k.messageContent.includes("waving-hand.png") ? icon({ iconName: "thumb_up", iconColor: "icon-safety-orange", iconSize: "size-64" }) : null}
                    {!isNullOrEmpty(k.deletedBy) ? (
                      <Box className={`flex flex-direction-row message-text-receiver align-items-center ${k.messageType === 2 && k.deletedBy !== "Both" && k.messageContent && !k.messageContent.includes("waving-hand.png") ? "no-after" : ""}`} sx={{ width: "unset !important" }}>
                        {icon({ iconName: "delete", iconColor: `${k.recipientUcnum === receiverID ? "text-refined-gray" : "text-secondary"}` })}
                        <Typography variant="body2" className={`${k.recipientUcnum === receiverID ? "text-refined-gray" : "text-secondary"} pad-l-8 italic`}>
                          {k.deletedBy === senderID || (k.deletedBy === "Both" && k.recipientUcnum === receiverID) ? "You deleted this message" : "Message deleted"}
                        </Typography>
                      </Box>
                    ) : k.messageType === 3 ? (
                      <Box
                        className={`bor-t-r-b-l-4 overflow-h pos-rel mdc-soft-show cur-pot`}
                        onClick={(e: any) => {
                          if (isBrowser) {
                            e.stopPropagation();
                            onPostChatClick(k.messageContent.eSysObjectID, k.messageContent.postLink);
                          }
                        }}
                      >
                        {k.recipientUcnum !== receiverID ? (
                          <Box className={`display-flex align-items-center pad-l-8 pad-r-8 pad-t-4 pad-b-4 ${k.recipientUcnum === receiverID ? "bg-periwinkle-crystal" : "bg-extra-light-gray"}`}>
                            <Avatar alt={k.messageContent.name} sx={{ fontSize: "10px", bgcolor: k.messageContent.profilePhotoColor, width: 16, height: 16 }} src={k.messageContent.profilePhoto} />
                            <Typography variant="subtitle2" className="text-primary mar-l-8 clamp-1" sx={{ lineHeight: 1 }}>
                              {k.messageContent.name}
                            </Typography>
                          </Box>
                        ) : null}
                        <Box className={`pos-rel ${!k.messageContent.postCaption ? "mar-b-24" : ""}`}>
                          <img alt={k.messageContent.name} src={k.messageContent.postImage} style={{ width: isTablet ? "180px" : "144px", height: isTablet ? "240px" : "191px", objectFit: "cover", objectPosition: "center" }} />
                          <Box className="box-24 pos-abs" sx={{ bottom: "8px", right: "8px" }}>
                            {icon({ iconName: "play_circle", iconColor: "icon-true-white" })}
                          </Box>
                        </Box>
                        {k.messageContent.postCaption ? (
                          <Box className={`display-flex align-items-center pad-l-8 pad-r-8 pad-t-4 pad-b-4 mar-b-24 bor-b-l-r bor-b-r-r ${k.recipientUcnum === receiverID ? "bg-periwinkle-crystal" : "bg-extra-light-gray"}`}>
                            <Typography variant="caption" className={`${isTablet ? "14px" : ""} text-primary clamp-2 ft-wt-500`}>
                              {k.messageContent.postCaption}
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>
                    ) : k.messageType === 4 ? (
                      <Box
                        className={`${k.parent ? "" : "bor-t-r-b-l-4"} overflow-h pos-rel mdc-soft-show cur-pot flex flex-direction-column`}
                        data-link={k.messageContent.url}
                        data-type={k.messageType}
                        onClick={(e) => {
                          if (isBrowser) {
                            e.stopPropagation();
                            onLinkChatClick(k.messageContent.url);
                          }
                        }}
                      >
                        {k.messageContent.image ? <img alt={k.messageContent.title} src={k.messageContent.image} style={{ width: isTablet ? "240px" : "216px", height: isTablet ? "240px" : "216px", objectFit: "cover", objectPosition: "center" }} /> : null}
                        <Box className={`display-flex flex-direction-column pad-4 border-radius-bottom ${k.recipientUcnum === receiverID ? "bg-periwinkle-crystal" : "bg-extra-light-gray"}`} sx={{ maxWidth: isTablet ? "240px" : "216px" }}>
                          <Typography variant="caption" className={`ft-wt-500 text-primary clamp-2`}>
                            {k.messageContent.title}
                          </Typography>
                          <Typography variant="caption" className={`ft-11 text-secondary clamp-1`}>
                            {k.messageContent.description}
                          </Typography>
                          <Typography variant="caption" className="ft-10 text-primary-color clamp-1">
                            {k.messageContent.url}
                          </Typography>
                        </Box>
                        <Box className="flex flex-direction-row w-per-100 align-items-center" sx={{ maxWidth: isTablet ? "240px" : "216px", minHeight: "34px" }}>
                          <div
                            className={`${k.recipientUcnum === receiverID ? "message-text" : "message-text-receiver"} display-block text-primary pad-l-4 pad-r-4`}
                            style={{ maxWidth: isTablet ? "240px" : "216px", marginRight: "unset" }}
                            dangerouslySetInnerHTML={{
                              __html: HtmlDecode(replaceAllData(renderToString(<Linkify options={{ render: renderLink, className: `text-primary-color` }}>{replaceAllData(k.messageContent.messageContent, CDN_ONE_FILE_PATH, "***CDN_ONE_FILE_PATH***")}</Linkify>), "***CDN_ONE_FILE_PATH***", CDN_ONE_FILE_PATH)),
                            }}
                          ></div>
                        </Box>
                      </Box>
                    ) : k.messageType !== 2 || (k.messageContent && k.messageContent.includes("waving-hand.png")) ? (
                      <Box className="flex flex-direction-row w-per-100 align-items-center" sx={{ minHeight: "34px" }}>
                        <div
                          className={`${k.recipientUcnum === receiverID ? "message-text" : "message-text-receiver"} display-block text-primary pad-l-4 pad-r-4`}
                          dangerouslySetInnerHTML={{
                            __html: HtmlDecode(replaceAllData(renderToString(<Linkify options={{ render: renderLink, className: `text-primary-color` }}>{replaceAllData(k.messageContent, CDN_ONE_FILE_PATH, "***CDN_ONE_FILE_PATH***")}</Linkify>), "***CDN_ONE_FILE_PATH***", CDN_ONE_FILE_PATH)),
                          }}
                        ></div>
                      </Box>
                    ) : null}
                    {!k.notSent && !adminIsBlocked && !blockedAccount && (k.messageType !== 2 || (k.messageContent && k.messageContent.includes("waving-hand.png"))) ? ( //isNullOrEmpty(k.deletedBy) &&
                      <Box
                        className="options-menu pos-abs box-32 pad-4 flex align-items-center op-0 cur-pot bor-rad-50"
                        sx={{
                          top: k.messageType === 4 ? (k.messageContent.image ? "8px" : "4px") : k.messageType === 3 ? (k.recipientUcnum === receiverID ? "8px" : "32px") : "4px",
                          right: k.messageType === 3 || k.messageType === 4 ? "8px" : "0px",
                          background: k.messageType === 3 || (k.messageType === 4 && k.messageContent.image) || (k.parent && k.parent.length > 0) ? "transparent radial-gradient(closest-side at 50% 50%, #00000033 0%, #00000000 100%) 0% 0% no-repeat padding-box;" : (k.messageType === 4 && !k.messageContent.image) || (k.parent && k.parent.length > 0) ? "unset" : `radial-gradient(at top right, ${k.recipientUcnum === receiverID ? "rgba(208, 231, 255, 1)" : "rgba(247, 247, 247, 1)"} 60%, ${k.recipientUcnum === receiverID ? "rgba(208, 231, 255, 0)" : "rgba(247, 247, 247, 0)"} 80%)`,
                          zIndex: 1,
                        }}
                        onClick={(e: any) => onChatOptionsClick(e, i, k.messageID)}
                      >
                        {icon({ iconName: "keyboard_arrow_down", iconColor: `${k.messageType === 3 || (k.messageType === 4 && k.messageContent.image) || (k.parent && k.parent.length > 0) ? "icon-true-white" : "icon-modern-gray"}`, iconSize: "size-24" })}
                      </Box>
                    ) : null}
                    {k.messageType === 2 && k.deletedBy !== "Both" && k.messageContent && !k.messageContent.includes("waving-hand.png") ? (
                      <Box className={`cur-pot-none pad-l-8 pad-r-8 pad-t-4 pad-b-4 flex flex-direction-column bor-t-r-b-l-4 ${k.recipientUcnum === receiverID ? "flex-align-self-end" : "flex-align-self-start"}`} sx={{ backgroundColor: k.recipientUcnum === receiverID ? "var(--color-pale-cornflower-blue)" : "var(--color-ultra-light-gray)", boxShadow: "0px 1px 0px #0000003d" }}>
                        <Box className="flex-align-self-end flex-direction-row align-items-center ht-16">
                          <Typography variant="overline" className={`${k.recipientUcnum === receiverID ? "text-refined-gray" : "text-secondary"} ft-11 mar-r-4`} sx={{ lineHeight: "9px" }}>
                            {formatDateTimeMessage(k.sentAt)}
                          </Typography>
                          {k.recipientUcnum === receiverID && !k.notSent && isNullOrEmpty(k.deletedBy) ? (k.messageStatus === 0 ? icon({ iconName: "schedule", iconColor: "icon-refined-gray", iconSize: "size-16" }) : k.messageStatus === 1 ? icon({ iconName: "check", iconColor: "icon-refined-gray", iconSize: "size-16" }) : icon({ iconName: "done_all", iconColor: `${k.isRead ? "icon-primary-color" : "icon-refined-gray"}`, iconSize: "size-16" })) : null}
                        </Box>
                      </Box>
                    ) : (
                      <Box className="pos-abs flex-align-self-end flex-direction-row align-items-center cur-pot-none ht-16" sx={{ bottom: "4px", right: "8px" }}>
                        <Typography variant="overline" className={`${k.recipientUcnum === receiverID ? "text-refined-gray" : "text-secondary"} ft-11 mar-r-4`} sx={{ lineHeight: "9px" }}>
                          {formatDateTimeMessage(k.sentAt)}
                        </Typography>
                        {k.recipientUcnum === receiverID && !k.notSent && isNullOrEmpty(k.deletedBy) ? (k.messageStatus === 0 ? icon({ iconName: "schedule", iconColor: "icon-refined-gray", iconSize: "size-16" }) : k.messageStatus === 1 ? icon({ iconName: "check", iconColor: "icon-refined-gray", iconSize: "size-16" }) : icon({ iconName: "done_all", iconColor: `${k.isRead ? "icon-primary-color" : "icon-refined-gray"}`, iconSize: "size-16" })) : null}
                      </Box>
                    )}
                  </Box>
                  {isBrowser && isNullOrEmpty(k.deletedBy) && !k.notSent && !adminIsBlocked && !blockedAccount ? (
                    <Box className={`${k.recipientUcnum === receiverID ? "mar-r-8" : "mar-l-8"}`}>
                      {k.messageType === 2 && k.deletedBy !== "Both" && k.messageContent && !k.messageContent.includes("waving-hand.png") ? (
                        <Box className={`options-menu box-32 flex align-items-center op-0 cur-pot bor-rad-50`} onClick={(e: any) => onChatOptionsClick(e, i, k.messageID)}>
                          <IconButton className={`box-32`}>{icon({ iconName: "keyboard_arrow_down", iconColor: "icon-quick-silver", iconSize: "size-24" })}</IconButton>
                        </Box>
                      ) : null}
                      <IconButton onClick={(e: any) => onReactionsClick(e, i, k.messageID)} className={`reaction-menu box-32 mar-t-4 op-0`}>
                        {icon({ iconName: "sentiment_satisfied_alt", iconColor: "icon-quick-silver" })}
                      </IconButton>
                    </Box>
                  ) : null}
                </Box>
                {(k.reactions && k.reactions.length > 0 && isNullOrEmpty(k.deletedBy)) || (k.reactions && k.reactions.length > 0 && !isNullOrEmpty(k.deletedBy) && k.deletedBy === k.recipientUcnum) ? (
                  <Box className={`cur-pot mdc-box-shadow-2 bg-true-white display-flex-center pad-2 mar-b-2 mdc-typography--border ${k.recipientUcnum === receiverID ? "flex-direction-row-reverse flex-align-self-end mar-r-16" : "flex-direction-row flex-align-self-start mar-l-16"} `} style={{ marginTop: "-4px", boxShadow: "0px 1px 0px #0000003D", zIndex: 2, borderRadius: "12px" }} onClick={(e: any) => onReactionListClick(e, k.messageID)}>
                    {k.reactions.map((m: any, u: number) => {
                      m.reaction = Decrypt(m.reaction);
                      if (isIOS) {
                        m.reaction = replaceAllData(replaceAllData(m.reaction, `src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=`, `srcset="" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAgMBAhS+BpQAAAAASUVORK5CYII="`), `background-size: 6100% 6100%`, `object-fit: none; background-size: 6100% 6100%`);
                      } else {
                        m.reaction = replaceAllData(replaceAllData(replaceAllData(m.reaction, `src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAgMBAhS+BpQAAAAASUVORK5CYII="`, `src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="`), `srcset=""`, ""), `srcSet=""`, "");
                      }
                      return <Box key={u} className={`ht-16 flex align-items-center`} dangerouslySetInnerHTML={{ __html: m.reaction }}></Box>;
                    })}
                  </Box>
                ) : null}
                {chatOptionsAnchor && chatOptionsAnchor.msgID === k.messageID && <CreateMenu anchorElement={chatOptionsAnchor.target} setAnchorElement={setChatOptionsAnchor} menuItemList={chatOptionsItems} ref={chatMenuRef} />}
                {chatReactionsAnchor && chatReactionsAnchor.msgID === k.messageID && <CreateReactionsMenu anchorElement={chatReactionsAnchor.target} setAnchorElement={setChatReactionsAnchor} onSelect={(key: string) => onReactionsSelect(k.messageID, key)} index={chatReactionsAnchor.reactionIndex} />}
                {chatReactionListAnchor && chatReactionListAnchor.msgID === k.messageID && <ShowReactions ref={chatReactionRef} messageID={k.messageID} reactionsProp={k.reactions ? k.reactions : []} isOpen={!isNullOrEmpty(chatReactionListAnchor.target)} senderID={senderID} senderDetails={senderProfileDetails} onUpdateReaction={onUpdateReaction} onClose={() => setChatReactionListAnchor(null)} />}
                {k.notSent && (
                  <Typography variant="body2" className="text-secondary text-align-r pad-t-8 pad-b-8">
                    {resource.deliverPrivacySettings}
                  </Typography>
                )}
              </Box>
            ) : null;
          })}
        </Box>
      )}
      {isAllowedToSendMessage() && messages.length === 0 && <ChatSendHi onWavingHandClick={onWavingHandClick} />}
    </Box>
  );
};

export default forwardRef(ChatMessage);
